import React from "react";
import clsx from "clsx";

import {
  ComposedChart,
  Bar,
  Tooltip,
  ResponsiveContainer,
  YAxis,
  XAxis,
  CartesianGrid,
  Cell,
  Line,
} from "recharts";

import {
  selectFormat,
  CustomizedYAxisTick,
  StandardTooltip,
  Error,
  getAllKeys,
  MAIN_COLORS,
  getSpecifiedLegendOrder,
} from "./../Utils/Utils.js";

export const COLORS = {
  Linear: MAIN_COLORS[0],
  Digital: MAIN_COLORS[1],
  Live: MAIN_COLORS[0],
  Highlight: MAIN_COLORS[1],
  Replay: MAIN_COLORS[2],
  Cumulative: "#868E96",
  "2018 Video Starts": "#868E96",
  "2019 Video Starts": "#868E96",
  "Virtual Channel": MAIN_COLORS[3],
};

const WorldCupDualAxisBarChartPhases = (props) => {
  const {
    chart: data,
    phases,
    // totalValueToDate
  } = props.data;

  if (Object.keys(data).length <= 1) {
    return <Error minHeight={360} />;
  }

  // For Phase
  const getPhaseWidths = () => {
    const activePhases = phases.filter((phase) => phase.highlight === true);
    if (activePhases.length && data.length) {
      activePhases.unshift({
        start: data[0].date,
        end: activePhases[0].start,
      });
    }

    return activePhases.map((el, i) => {
      const endIndex = data.findIndex((d) => d.date === el.end);
      const duration =
        (endIndex < 0 ? data.length - 1 : endIndex) -
        data.findIndex((d) => d.date === el.start) +
        1 +
        (i === 0 ? -1 : 0); // need for the first empty phase

      return {
        name: el.name,
        width: (duration < 0 ? 0 : (duration / data.length) * 100) + "%",
      };
    });
  };

  const phaseWidths = getPhaseWidths(phases);

  const renderBars = (data) => {
    let bars = [];
    let all_bars = [];

    const sortedKeys = getSortedKeys(data);

    for (let i = 0; i <= data.length - 1; i++) {
      sortedKeys.forEach((source) => all_bars.push(source));
    }

    const isFixedBarWidth = data.length <= 7;

    [...new Set(all_bars)].forEach((bar, i) => {
      // exclude Cumulative (line) && 2018 Video Starts
      if (
        bar !== "Cumulative" &&
        bar !== "2018 Video Starts" &&
        bar !== "2019 Video Starts"
      )
        bars.push(
          <Bar
            key={i}
            dataKey={bar}
            stackId="stack"
            barSize={isFixedBarWidth ? 35 : null}
            fill={COLORS[bar]}
            yAxisId="left"
          >
            {data.map(
              (entry, index) =>
                entry && (
                  <Cell
                    key={`cell-${bar}-${index}`}
                    fillOpacity={entry[bar] < 0 ? 0.5 : 1}
                  />
                )
            )}
          </Bar>
        );
    });

    return bars;
  };

  const legendElement = (data) =>
    data.map((entry, i) => (
      <span key={i} className="legend mt-2 mt-md-0">
        <span
          className="legend__icon"
          style={{ background: COLORS[entry] }}
        ></span>
        <span className="legend__text" style={{ color: COLORS[entry] }}>
          {entry.replace(/_/g, " ")}
        </span>
      </span>
    ));

  const renderLegends = (data) => {
    return legendElement([
      ...new Set(
        getSpecifiedLegendOrder(getSortedKeys(data), ["2018 Video Starts"])
      ),
    ]);
  };

  const getSortedKeys = (_data) => {
    if (!_data) return [];
    if (!_data[0]) return [];

    const data = _data.filter((d) => d);

    const keys = getAllKeys(data, [
      "date",
      "Cumulative",
      "2018 Video Starts",
      "2019 Video Starts",
    ]);

    // Support custom metrics
    const has_line_cumulative = data[1].hasOwnProperty("Cumulative");
    const include_line_cumulative = has_line_cumulative ? ["Cumulative"] : "";

    const has_line_2018 = data[1].hasOwnProperty("2018 Video Starts");
    const has_line_2019 = data[1].hasOwnProperty("2019 Video Starts");

    const include_line_2018 = has_line_2018 ? ["2018 Video Starts"] : null;
    const include_line_2019 = has_line_2019 ? ["2019 Video Starts"] : null;

    const len = data.length;

    return keys
      .map((key) => ({
        key: key,
        value: data.reduce((accum, curr) => accum + (curr[key] || 0), 0) / len,
      }))
      .sort((a, b) => b.value - a.value)
      .map((d) => d.key)
      .concat(
        include_line_cumulative || include_line_2018 || include_line_2019
      );
  };

  const renderPhaseTitles = () => (
    <div className="d-flex phase-titles">
      {phaseWidths.map((el, i) => (
        <div
          key={i}
          style={{ width: el.width }}
          className={clsx("phase-titles__item", { "no-boder": i === 0 })}
        >
          <span>{el.name}</span>
        </div>
      ))}
    </div>
  );

  const getPhaseStyles = (el, i) => ({
    width: el.width,
    backgroundColor: i % 2 === 1 ? "#FEF0EE" : "",
  });

  const formatTooltipValue = (key, data) => {
    let formatted_value = "";
    switch (key) {
      default:
        formatted_value = selectFormat("number", data[key]);
    }

    return formatted_value;
  };

  const renderChart = (data) => (
    <>
      {renderLegends(data)}

      <div className="composed-chart position-relative mt-3">
        {/*  Phase Background */}
        <div className="composed-chart__phase">
          {phaseWidths.map((el, i) => (
            <div
              key={i}
              className="composed-chart__phase--item"
              style={getPhaseStyles(el, i)}
            />
          ))}
        </div>

        <ResponsiveContainer height={350} width="100%">
          <ComposedChart
            data={data}
            margin={{ top: 12, right: 0, bottom: 0, left: -38 }}
            stackOffset="sign"
          >
            <CartesianGrid
              stroke="#FEE5DE"
              strokeDasharray="3 3"
              vertical={false}
            />

            <Tooltip
              cursor={{ fill: "#eee", opacity: 0.5 }}
              content={
                <StandardTooltip
                  render={({ data }) => {
                    const payload = data(props);

                    if (payload !== null) {
                      const tooltip_data = payload[0].payload;

                      let total = "";
                      let cumulative = "";
                      let video_starts = "";

                      if (tooltip_data.total) {
                        total = tooltip_data.total;
                      } else {
                        total = payload
                          .filter(
                            (metric) =>
                              metric.name !== "Cumulative" &&
                              metric.name !== "2018 Video Starts" &&
                              metric.name !== "2019 Video Starts"
                          )
                          .reduce((acc, cur) => acc + (cur.value || 0), 0);

                        cumulative = payload.filter(
                          (metric) => metric.name === "Cumulative"
                        )[0]?.value;

                        video_starts = payload.filter(
                          (metric) =>
                            metric.name === "2018 Video Starts" ||
                            metric.name === "2019 Video Starts"
                        )[0]?.value;
                      }

                      return (
                        <div className="standard-tooltip">
                          {tooltip_data.date && (
                            <header className="standard-tooltip__header">
                              <span className="standard-tooltip__key-header">
                                Date:
                              </span>
                              <span className="standard-tooltip__value-header">
                                {tooltip_data.date}
                              </span>
                            </header>
                          )}
                          <main className="standard-tooltip__main">
                            {Object.keys(tooltip_data)
                              .filter(
                                (key) =>
                                  key !== "date" &&
                                  key !== "Cumulative" &&
                                  key !== "2018 Video Starts" &&
                                  key !== "2019 Video Starts"
                              )
                              .map((key, index) => (
                                <div
                                  className="standard-tooltip__item"
                                  key={key}
                                >
                                  <span
                                    className="standard-tooltip__icon"
                                    style={{ background: COLORS[key] }}
                                  ></span>
                                  <span
                                    className="standard-tooltip__key text-capitalize"
                                    style={{ color: COLORS[key] }}
                                  >
                                    {key.replace(/_/g, " ")}
                                  </span>
                                  <span className="standard-tooltip__value">
                                    {formatTooltipValue(key, tooltip_data)}
                                  </span>
                                </div>
                              ))}

                            <div className="standard-tooltip__dilimiter1"></div>

                            <div className="standard-tooltip__item">
                              <span className="standard-tooltip__key text-capitalize total">
                                Total{" "}
                              </span>
                              <span className="standard-tooltip__value">
                                {selectFormat("number", total)}
                              </span>
                            </div>

                            {cumulative && (
                              <>
                                <div className="standard-tooltip__dilimiter1"></div>
                                <div className="standard-tooltip__item">
                                  <span className="standard-tooltip__key text-capitalize total">
                                    Cumulative{" "}
                                  </span>
                                  <span className="standard-tooltip__value">
                                    {selectFormat("number", cumulative)}
                                  </span>
                                </div>
                              </>
                            )}

                            {video_starts && (
                              <>
                                <div className="standard-tooltip__dilimiter1"></div>
                                <div className="standard-tooltip__item">
                                  <span className="standard-tooltip__key text-capitalize total">
                                    {/* Show 2018 or 2019 based on condition */}
                                    {tooltip_data["2018 Video Starts"]
                                      ? "2018 Video Starts"
                                      : "2019 Video Starts"}{" "}
                                  </span>
                                  <span className="standard-tooltip__value">
                                    {selectFormat("number", video_starts)}
                                  </span>
                                </div>
                              </>
                            )}
                          </main>
                        </div>
                      );
                    }
                  }}
                />
              }
            />

            {renderBars(data)}

            <Line
              yAxisId="right"
              type="monotone"
              dataKey="Cumulative"
              stroke={COLORS["Cumulative"]}
            />

            <Line
              yAxisId="right"
              type="monotone"
              dataKey="2018 Video Starts"
              stroke={COLORS["2018 Video Starts"]}
            />

            <Line
              yAxisId="right"
              type="monotone"
              dataKey="2019 Video Starts"
              stroke={COLORS["2019 Video Starts"]}
            />

            <XAxis
              dataKey="date"
              tickLine={false}
              axisLine={false}
              scale="auto"
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tickFormatter={(d) => selectFormat("number", d)}
              tick={<CustomizedYAxisTick />}
              yAxisId="left"
              orientation="left"
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tickFormatter={(d) => selectFormat("number", d)}
              tick={<CustomizedYAxisTick />}
              yAxisId="right"
              orientation="right"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {renderPhaseTitles()}
    </>
  );

  return renderChart(data);
};

export default WorldCupDualAxisBarChartPhases;
